import * as React from 'react';
import { ChevronDownSmall as ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import { TextButton, TextButtonPriority } from '../../TextButton';
import { ReactionButtonProps, ReactionButtonRef } from '../types';
import { classes, st } from './ReactionButton.st.css';
import { ReactionsDataHooks } from '../dataHooks';

const TIMER_DELAY = 500;

export const ReactionButton = React.forwardRef<
  ReactionButtonRef,
  ReactionButtonProps
>(
  (
    {
      className,
      selectedReaction,
      active,
      onTriggerReaction,
      onTriggerPopover,
      popoverTriggerAriaLabel,
      mobile,
      expanded,
    },
    ref,
  ) => {
    const [animate, setAnimate] = React.useState<boolean>(false);
    const [isTriggerVisible, setTriggerVisibility] =
      React.useState<boolean>(false);
    const subTriggerRef = React.useRef<HTMLButtonElement>();
    const triggerTimer = React.useRef<number>();
    const isPressedKey = React.useRef<boolean>(false);
    const rootRef = React.useRef<HTMLDivElement>();
    const animationFrame = React.useRef<number>();
    const doesMainButtonTriggerPopover = mobile && !active;
    const ariaProps: any = doesMainButtonTriggerPopover
      ? {
          'aria-haspopup': 'menu',
          'aria-expanded': expanded,
        }
      : {
          'aria-pressed': active,
        };

    const clearTimer = React.useCallback(
      () => clearTimeout(triggerTimer.current),
      [],
    );

    React.useImperativeHandle(
      ref,
      () => ({
        cancelPopover: clearTimer,
        focus: () => subTriggerRef.current?.focus(),
      }),
      [clearTimer],
    );

    const onAnimationEnd = () => {
      setAnimate(false);
    };

    const showTriggerButton = () => {
      setTriggerVisibility(true);
    };

    const hideTriggerButton = () => {
      // Don't hide if popover is open
      if (!expanded) {
        setTriggerVisibility(false);
      }
    };

    const onFocus = () => {
      if (isPressedKey.current) {
        cancelAnimationFrame(animationFrame.current);
        showTriggerButton();
      }
    };
    const onBlur = () => {
      if (
        isPressedKey.current &&
        !rootRef.current.contains(document.activeElement)
      ) {
        animationFrame.current = requestAnimationFrame(() => {
          hideTriggerButton();
        });
      }
    };

    const onClick = (e) => {
      if (doesMainButtonTriggerPopover) {
        onTriggerPopover();
      } else {
        // prevent focus on button
        e.preventDefault();
        onTriggerReaction();
      }
    };

    const onMouseEnter = () => {
      triggerTimer.current = window.setTimeout(onTriggerPopover, TIMER_DELAY);
    };

    React.useEffect(() => {
      if (active) {
        setAnimate(true);
      }
    }, [active, selectedReaction.id]);

    React.useEffect(() => {
      let documentListenerTimer: number;

      const onDocumentKeyDown = () => {
        isPressedKey.current = true;
        documentListenerTimer = requestAnimationFrame(
          () => (isPressedKey.current = false),
        );
      };

      document.addEventListener('keydown', onDocumentKeyDown);

      return () => {
        clearTimer();
        cancelAnimationFrame(documentListenerTimer);
        document.removeEventListener('keydown', onDocumentKeyDown);
      };
    }, [clearTimer]);

    return (
      <div
        onFocus={!mobile ? onFocus : undefined}
        onBlur={!mobile ? onBlur : undefined}
        ref={rootRef}
        className={st(
          classes.root,
          {
            defaultReaction: selectedReaction.id,
            active,
            animate,
            visibleTrigger: isTriggerVisible,
          },
          className,
        )}
      >
        <TextButton
          className={classes.reactionButton}
          onClick={onClick}
          onMouseEnter={!mobile ? onMouseEnter : undefined}
          onMouseLeave={!mobile ? clearTimer : undefined}
          {...ariaProps}
          prefixIcon={React.cloneElement(selectedReaction.icon, {
            onAnimationEnd,
          })}
          priority={TextButtonPriority.secondary}
          data-hook={`${ReactionsDataHooks.TriggerButton} ${
            active ? ReactionsDataHooks.HasReaction : ''
          }`}
        >
          <span data-hook={ReactionsDataHooks.ReactionLabel}>
            {selectedReaction.label}
          </span>
        </TextButton>
        {!mobile ? (
          <button
            className={classes.popoverTrigger}
            onClick={onTriggerPopover}
            ref={subTriggerRef}
            aria-label={popoverTriggerAriaLabel}
            aria-haspopup="menu"
            aria-expanded={expanded}
            data-hook={ReactionsDataHooks.SubTriggerButton}
          >
            <ChevronDown />
          </button>
        ) : null}
      </div>
    );
  },
);
